/**
 * RoQeet Hub 순수 로직 — 결정 #23/#31. 플랫폼 중립(결정 #10): 네트워크/DOM 의존
 * 없이 "발행 입력 만들기·검증", "등급 게이팅 필터", "받은 페이로드 → 도메인 엔티티
 * 디코드"만 담당한다. 실제 백엔드 호출은 web의 Supabase 어댑터(HubClient 포트)가 한다.
 *
 * 설계 핵심: 허브 페이로드는 **기존 교환 포맷(결정 #6)을 그대로 재사용**한다. 발행은
 * export 함수로 직렬화하고, 설치는 import 함수로 복원한다 — 즉 허브는 "원격 import/
 * export 저장소"이고, 받는 쪽은 평소 파일 가져오기와 동일한 검증 경로를 탄다. 덕분에
 * 보안 검증(결정 #17 화이트리스트)이 한 곳에 모이고 새 신뢰 경계가 생기지 않는다.
 */
import type {
  Character,
  ContentRating,
  GameCard,
  Lorebook,
  Preset,
  ShareMeta,
} from "../domain/index.js";
import type {
  HubContentKind,
  HubListing,
  HubPublishInput,
} from "../domain/hub.js";
import { exportCardToJson } from "../import/card-export.js";
import { importCardFromBytes } from "../import/card.js";
import { exportLorebookToJson, importLorebookFromJson } from "../import/lorebook-io.js";
import { exportPresetToJson, importPresetFromJson } from "../import/preset-io.js";
import { exportGameCardToJson, importGameCardFromJson } from "../import/game-card-io.js";

export {
  HUB_ITEM_KINDS,
  HUB_CREATOR_HANDLE_PATTERN,
  HUB_CREATOR_UNKNOWN_DISPLAY_NAME,
  HUB_ITEM_STATS_DEFAULTS,
  anonymousHubSession,
  fallbackHubCreatorDisplayName,
  getHubItemDisplayLabel,
  getHubItemParentDependencyLabel,
  hubItemDtoFromListing,
  hubItemKindForDisplay,
  hubItemKindFromLegacyContentKind,
  isHubCreatorHandle,
  isHubItemKind,
  legacyContentKindFromHubItemKind,
  memberHubSession,
  normalizeHubMember,
  normalizeHubCreatorHandle,
  normalizeHubItemDto,
  normalizeHubItemCreator,
  normalizeHubItemStats,
  normalizeHubPublicProfile,
  publicProfileFromHubProfile,
  type BotSubtype,
  type HubAuthState,
  type HubItemCreatorDto,
  type HubItemDisplayInput,
  type HubItemDto,
  type HubItemKind,
  type HubItemParentSummaryDto,
  type HubItemStatsDto,
  type HubItemStatus,
  type HubItemVisibility,
  type HubMemberDto,
  type HubPublicProfileDto,
  type HubSessionDto,
} from "../domain/hub.js";

/** 평점 별점 범위(1~5). */
export const MIN_RATING_STARS = 1;
export const MAX_RATING_STARS = 5;

/** 유효한 콘텐츠 종류인지(런타임 가드 — 백엔드 응답 검증용). */
export function isHubContentKind(v: unknown): v is HubContentKind {
  return v === "character" || v === "lorebook" || v === "preset" || v === "game-card";
}

/** 유효한 콘텐츠 등급인지(런타임 가드). */
export function isContentRating(v: unknown): v is ContentRating {
  return v === "sfw" || v === "nsfw" || v === "unrated";
}

/**
 * 별점 평점을 1~5 정수로 정규화한다(범위 밖이면 throw). 어댑터가 백엔드로 보내기 전,
 * UI가 받기 전 양쪽에서 호출해 일관성을 보장한다.
 */
export function normalizeStars(stars: number): number {
  const n = Math.round(stars);
  if (!Number.isFinite(n) || n < MIN_RATING_STARS || n > MAX_RATING_STARS) {
    throw new Error("lint.comm.invalidRatingRange");
  }
  return n;
}

/** ShareMeta + 기본값에서 발행 메타 일부를 끌어온다(빈칸 자동 채움). */
function metaFromShare(share: ShareMeta | undefined): Pick<HubPublishInput, "version" | "license"> {
  return {
    ...(share?.creatorVersion ? { version: share.creatorVersion } : {}),
    ...(share?.license ? { license: share.license } : {}),
  };
}

/** 발행 입력 공통 필드(폼에서 사용자가 채우는 부분)의 오버라이드. */
export interface PublishMetaOverride {
  title?: string;
  summary?: string;
  description?: string;
  tags?: string[];
  rating?: ContentRating;
  version?: string;
  license?: string;
  /** 재발행이면 이전 publicId(업데이트로 인식). */
  publicId?: string;
  sourceVisibility?: "public" | "hidden";
  statsPrivacy?: "public" | "private" | "uncollected";
}

/**
 * 캐릭터를 발행 입력으로 변환한다(결정 #6/#23). 페이로드는 CCv3 JSON. 메타는 캐릭터
 * 필드(name/tags/rating/share)에서 끌어오고, override로 덮을 수 있다. 임베드 로어북이
 * 있으면 호출부가 lorebook을 넘겨 character_book으로 함께 싣는다.
 */
export function buildCharacterPublish(
  character: Character,
  override: PublishMetaOverride = {},
  lorebook?: Lorebook,
): HubPublishInput {
  return finalizePublish({
    kind: "character",
    title: override.title ?? character.name,
    ...(override.summary ? { summary: override.summary } : {}),
    ...(override.description ?? character.creatorNotes
      ? { description: override.description ?? character.creatorNotes }
      : {}),
    tags: override.tags ?? character.tags ?? [],
    rating: override.rating ?? character.rating ?? "unrated",
    ...metaFromShare(character.share),
    ...(override.version ? { version: override.version } : {}),
    ...(override.license ? { license: override.license } : {}),
    ...(override.publicId ?? character.share?.publicId
      ? { publicId: override.publicId ?? character.share?.publicId }
      : {}),
    ...(override?.sourceVisibility ? { sourceVisibility: override.sourceVisibility } : {}),
    ...(override?.statsPrivacy ? { statsPrivacy: override.statsPrivacy } : {}),
    payload: exportCardToJson(character, lorebook),
  });
}

/** 로어북을 발행 입력으로 변환한다(결정 #6/#23). */
export function buildLorebookPublish(
  book: Lorebook,
  override: PublishMetaOverride = {},
): HubPublishInput {
  return finalizePublish({
    kind: "lorebook",
    title: override.title ?? book.name,
    ...(override.summary ? { summary: override.summary } : {}),
    ...(override.description ?? book.description
      ? { description: override.description ?? book.description }
      : {}),
    tags: override.tags ?? book.share?.tags ?? [],
    rating: override.rating ?? "unrated",
    ...metaFromShare(book.share),
    ...(override.version ? { version: override.version } : {}),
    ...(override.license ? { license: override.license } : {}),
    ...(override.publicId ?? book.share?.publicId
      ? { publicId: override.publicId ?? book.share?.publicId }
      : {}),
    ...(override?.sourceVisibility ? { sourceVisibility: override.sourceVisibility } : {}),
    ...(override?.statsPrivacy ? { statsPrivacy: override.statsPrivacy } : {}),
    payload: exportLorebookToJson(book),
  });
}

/** 프리셋을 발행 입력으로 변환한다(결정 #6/#23). 프리셋은 항상 SFW 성격. */
export function buildPresetPublish(
  preset: Preset,
  override: PublishMetaOverride = {},
): HubPublishInput {
  return finalizePublish({
    kind: "preset",
    title: override.title ?? preset.name,
    ...(override.summary ? { summary: override.summary } : {}),
    ...(override.description ? { description: override.description } : {}),
    tags: override.tags ?? preset.share?.tags ?? [],
    rating: override.rating ?? "sfw",
    ...metaFromShare(preset.share),
    ...(override.version ? { version: override.version } : {}),
    ...(override.license ? { license: override.license } : {}),
    ...(override.publicId ?? preset.share?.publicId
      ? { publicId: override.publicId ?? preset.share?.publicId }
      : {}),
    ...(override?.sourceVisibility ? { sourceVisibility: override.sourceVisibility } : {}),
    ...(override?.statsPrivacy ? { statsPrivacy: override.statsPrivacy } : {}),
    payload: exportPresetToJson(preset),
  });
}

/** 게임 카드를 발행 입력으로 변환한다(결정 #6/#23/#29). */
export function buildGameCardPublish(
  card: GameCard,
  override: PublishMetaOverride = {},
): HubPublishInput {
  return finalizePublish({
    kind: "game-card",
    title: override.title ?? card.definition.title,
    ...(override.summary ? { summary: override.summary } : {}),
    ...(override.description ?? card.definition.description
      ? { description: override.description ?? card.definition.description }
      : {}),
    tags: override.tags ?? card.share?.tags ?? [],
    rating: override.rating ?? card.rating ?? "unrated",
    ...metaFromShare(card.share),
    ...(override.version ? { version: override.version } : {}),
    ...(override.license ? { license: override.license } : {}),
    ...(override.publicId ?? card.share?.publicId
      ? { publicId: override.publicId ?? card.share?.publicId }
      : {}),
    ...(override?.sourceVisibility ? { sourceVisibility: override.sourceVisibility } : {}),
    ...(override?.statsPrivacy ? { statsPrivacy: override.statsPrivacy } : {}),
    payload: exportGameCardToJson(card),
  });
}

/** 발행 입력을 정규화+검증한다(빌더들의 공통 출구). 잘못된 입력은 throw. */
function finalizePublish(input: HubPublishInput): HubPublishInput {
  validatePublishInput(input);
  // 태그 정규화: 트림·소문자 비교 중복 제거·빈 값 제거·상한.
  const seen = new Set<string>();
  const tags: string[] = [];
  for (const raw of input.tags) {
    const t = raw.trim();
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    tags.push(t);
    if (tags.length >= 20) break;
  }
  return { ...input, title: input.title.trim(), tags };
}

/**
 * 발행 입력을 검증한다(결정 #23). 백엔드도 RLS/제약으로 막지만, 클라이언트에서
 * 먼저 걸러 명확한 오류를 준다(결정 #15 다층 방어 정신).
 */
export function validatePublishInput(input: HubPublishInput): void {
  if (!isHubContentKind(input.kind)) throw new Error("lint.comm.unknownKind");
  if (!input.title || !input.title.trim()) throw new Error("lint.comm.titleEmpty");
  if (input.title.length > 120) throw new Error("lint.comm.titleTooLong");
  if (!isContentRating(input.rating)) throw new Error("lint.comm.invalidRating");
  if (!input.payload || !input.payload.trim()) throw new Error("lint.comm.payloadEmpty");
  // 페이로드가 JSON인지(교환 포맷, 결정 #6) 최소 확인.
  try {
    JSON.parse(input.payload);
  } catch {
    throw new Error("lint.comm.invalidPayloadFormat");
  }
}

/**
 * NSFW 게이팅 필터(결정 #23/#31). 백엔드 질의에서 1차로 거르지만, 받은 목록에서도
 * 한 번 더 거른다(다층 방어, 결정 #15). includeNsfw=false면 "nsfw" 등급을 숨긴다.
 * "unrated"는 보수적으로 노출하되, 호출부가 정책에 따라 조정할 수 있다.
 */
export function filterListingsByRating(
  listings: HubListing[],
  includeNsfw: boolean,
): HubListing[] {
  if (includeNsfw) return listings;
  return listings.filter((l) => l.rating !== "nsfw");
}

/**
 * 연령 게이트를 통과해야 NSFW를 볼 수 있는지 판단한다(결정 #23/#31). UI/질의가 이
 * 한 함수로 일관되게 게이팅하도록 모은다. 인증 안 됐으면 항상 SFW만.
 */
export function effectiveIncludeNsfw(ageVerified: boolean, userWantsNsfw: boolean): boolean {
  return ageVerified && userWantsNsfw;
}

/** 디코드 결과: 종류별 도메인 엔티티(설치 전, 새 id가 이미 부여됨). */
export type DecodedListing =
  | { kind: "character"; character: Character; lorebook?: Lorebook; fromPng: boolean }
  | { kind: "lorebook"; lorebook: Lorebook }
  | { kind: "preset"; preset: Preset }
  | { kind: "game-card"; card: GameCard };

/**
 * 받은 발행물 페이로드를 도메인 엔티티로 디코드한다(결정 #6). 종류에 맞는 import
 * 함수를 태워 **평소 파일 가져오기와 동일한 검증 경로**를 통과시킨다 — import가 새
 * id/sync 메타를 부여하므로 설치 시 로컬 충돌이 없다. web 설치 액션이 이 결과를 받아
 * 저장(에셋 연결 등)만 처리한다(결정 #10 경계).
 */
export function decodeListingPayload(kind: HubContentKind, payload: string): DecodedListing {
  switch (kind) {
    case "character": {
      const bytes = new TextEncoder().encode(payload);
      const imported = importCardFromBytes(bytes);
      return {
        kind: "character",
        character: imported.character,
        ...(imported.lorebook ? { lorebook: imported.lorebook } : {}),
        fromPng: imported.fromPng,
      };
    }
    case "lorebook":
      return { kind: "lorebook", lorebook: importLorebookFromJson(payload) };
    case "preset":
      return { kind: "preset", preset: importPresetFromJson(payload) };
    case "game-card":
      return { kind: "game-card", card: importGameCardFromJson(payload) };
    default:
      throw new Error("lint.comm.unknownKind");
  }
}
